'use client';

import { type FocusEvent, type InputHTMLAttributes, useCallback, useState } from 'react';
import { IMaskInput } from 'react-imask';

import { InputField } from '@hrworks/sui-core/InputField';
import { useFormDefaults } from '@hrworks/sui-shared/components';

import type { PhoneFieldProps } from './PhoneField.types';

/**
 * Regular expression pattern for valid phone number characters.
 * Allows: +, digits (0-9), spaces, hyphens, parentheses
 */
const PHONE_MASK_PATTERN = /^[+0-9()\-\s]*$/;

/**
 * Extracts the clean phone number (digits and optional leading +) from display value.
 * Useful for API calls and validation.
 *
 * @example
 * getCleanPhoneNumber('+49 176 1234567') // => '+491761234567'
 * getCleanPhoneNumber('(030) 123-456') // => '030123456'
 */
export const getCleanPhoneNumber = (displayValue: string): string => {
  if (!displayValue) return '';
  const hasPlus = displayValue.startsWith('+');
  const digits = displayValue.replace(/\D/g, '');
  return hasPlus ? `+${digits}` : digits;
};

export const PhoneField = ({
  ref,
  defaultValue,
  name,
  onBlur,
  onFocus,
  onValueChange,
  onValueChangeFinished,
  size: sizeFromProps,
  value,
  label,
  helpText,
  placeholder,
  validationState,
  validationMessage,
  disabled: disabledFromProps,
  readOnly: readOnlyFromProps,
  mandatory,
  labelPlacement: labelPlacementFromProps,
  className,
  style,
  ...otherProps
}: PhoneFieldProps) => {
  const formDefaults = useFormDefaults();
  const disabled = disabledFromProps ?? formDefaults.disabled;
  const readOnly = readOnlyFromProps ?? formDefaults.readOnly;
  const size = sizeFromProps ?? formDefaults.size ?? 'medium';
  const labelPlacement = labelPlacementFromProps ?? formDefaults.labelPlacement;

  const [isInputFocused, setInputFocused] = useState(false);
  const [valueOnFocus, setValueOnFocus] = useState('');
  const [inputValue, setInputValue] = useState(defaultValue ?? '');

  const currentValue = value ?? inputValue;

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setInputFocused(false);
      if (valueOnFocus !== event.target.value) {
        onValueChangeFinished?.(event.target.value);
      }
      onBlur?.(event);
    },
    [onBlur, onValueChangeFinished, valueOnFocus],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setInputFocused(true);
      setValueOnFocus(event.target.value);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleAccept = useCallback(
    (newValue: string) => {
      onValueChange?.(newValue);
      if (value === undefined) {
        setInputValue(newValue);
      }
    },
    [onValueChange, value],
  );

  const getInputProps = (inputId: string): InputHTMLAttributes<HTMLInputElement> => ({
    autoComplete: 'tel',
    defaultValue: typeof defaultValue === 'string' && value === undefined ? defaultValue : undefined,
    id: inputId,
    inputMode: 'tel',
    name,
    onBlur: handleBlur,
    onFocus: handleFocus,
    placeholder,
    readOnly,
    value: currentValue,
    type: 'tel',
    ...otherProps,
  });

  return (
    <InputField
      label={label}
      placeholder={placeholder}
      hasInputFocus={isInputFocused}
      hasInputValue={!!currentValue}
      helpText={helpText}
      readOnly={readOnly}
      size={size}
      mandatory={mandatory}
      validationMessage={validationMessage}
      validationState={validationState}
      disabled={disabled}
      labelPlacement={labelPlacement}
      className={className}
      style={style}
      input={({ id: inputId, inputStyles, ariaDescribedBy }) => (
        <IMaskInput
          inputRef={ref}
          mask={PHONE_MASK_PATTERN}
          css={inputStyles}
          onAccept={handleAccept}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          {...getInputProps(inputId)}
        />
      )}
    />
  );
};
